"use client"

import Image from "next/image"
import Link from "next/link"
import { ArrowRight, CalendarDays, MapPin, Ticket } from "lucide-react"

import { Button } from "@/components/ui/button"
import { useIsMounted } from "@/hooks/use-is-mounted"
import {
    describeCountdown,
    hasSlotPassed,
    useFreeMockSlot,
} from "@/hooks/use-free-mock-slot"
import {
    EVENT_HREF,
    EVENT_POSTER,
    EVENT_POSTER_SIZE,
} from "@/lib/free-mock-event"

/**
 * Landing-page promo for the free in-person mock test.
 *
 * Date handling is the whole reason this is a client component. The landing
 * page is otherwise static, so a server-side date check would be frozen at
 * build time and the section would keep advertising the event long after it
 * happened. Instead the markup is identical on the server and on the first
 * client render — the urgency line is the only thing that changes, and only
 * once mounted, so hydration still matches.
 */
export function FreeMockBanner() {
    const mounted = useIsMounted()
    const { slot } = useFreeMockSlot()
    const passed = mounted && hasSlotPassed(slot.starts_at)

    // Once the date is gone the section stops being a promotion. Removing it
    // entirely would silently drop a link people may still be following, so it
    // stays and simply tells the truth.
    const urgency = mounted ? describeCountdown(slot.starts_at) : null

    return (
        <section
            aria-labelledby="free-mock-heading"
            className="relative overflow-hidden border-y border-border bg-muted/30 py-14 md:py-20"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[360px] w-[720px] max-w-full -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-[120px]"
            />

            <div className="container mx-auto px-4 sm:px-6">
                <div className="mx-auto grid max-w-5xl items-center gap-8 rounded-3xl border border-border bg-card p-6 sm:p-8 md:grid-cols-[minmax(0,260px)_1fr] md:gap-10 md:p-10">
                    <Link
                        href={EVENT_HREF}
                        tabIndex={-1}
                        aria-hidden="true"
                        className="mx-auto w-full max-w-[260px] overflow-hidden rounded-2xl border border-border shadow-lg"
                    >
                        <Image
                            src={slot.poster_url || EVENT_POSTER}
                            alt=""
                            width={EVENT_POSTER_SIZE.width}
                            height={EVENT_POSTER_SIZE.height}
                            sizes="(max-width: 768px) 60vw, 260px"
                            className="h-auto w-full object-cover"
                            unoptimized={Boolean(slot.poster_url?.startsWith("http"))}
                        />
                    </Link>

                    <div className="min-w-0 text-center md:text-left">
                        {/* How soon it is, computed from the slot's start. Only
                            rendered after mount — the server has no clock the
                            client could agree with. */}
                        {urgency && (
                            <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-primary">
                                <Ticket className="h-3.5 w-3.5" aria-hidden="true" />
                                {urgency}
                            </p>
                        )}

                        <h2
                            id="free-mock-heading"
                            className="mt-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl"
                        >
                            Sit an in-house mock test{" "}
                            <span className="text-primary">in person</span>
                        </h2>

                        <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg">
                            A full-length Digital SAT under real exam conditions — properly timed and
                            supervised. Free, and open to anyone preparing for the test.
                        </p>

                        <dl className="mt-5 flex flex-wrap justify-center gap-x-8 gap-y-2 md:justify-start">
                            <div className="flex items-center gap-2">
                                <CalendarDays className="h-4 w-4 text-primary" aria-hidden="true" />
                                <dt className="sr-only">Date</dt>
                                <dd className="text-sm font-semibold text-foreground">
                                    {slot.date_label}
                                </dd>
                            </div>
                            <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-primary" aria-hidden="true" />
                                <dt className="sr-only">Location</dt>
                                <dd className="text-sm font-semibold text-foreground">
                                    {slot.location_text}
                                </dd>
                            </div>
                        </dl>

                        <Button
                            asChild
                            size="lg"
                            className="mt-7 h-12 w-full rounded-full px-8 text-base sm:w-auto"
                        >
                            <Link href={EVENT_HREF}>
                                {passed ? "See the details" : "Apply for a spot"}
                                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                            </Link>
                        </Button>
                    </div>
                </div>
            </div>
        </section>
    )
}
